import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/i18n/LanguageContext';
import { useSignedUrls } from '@/hooks/useSignedUrl';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import celloScroll from '@/assets/cello-scroll-user-3840.jpg';

const HeroSection = () => {
  const { t } = useLanguage();
  const { settings } = useSiteSettings();

  const { data: heroInstrument } = useQuery({
    queryKey: ['hero-instrument'],
    queryFn: async () => {
      const { data } = await supabase
        .from('instruments')
        .select('id')
        .eq('homepage_eligible', true)
        .eq('status', 'published')
        .order('display_order')
        .limit(1);
      return data?.[0] || null;
    },
  });

  const { data: heroImage } = useQuery({
    queryKey: ['hero-instrument-image', heroInstrument?.id],
    queryFn: async () => {
      if (!heroInstrument) return null;
      const { data } = await supabase
        .from('gallery_images')
        .select('storage_path, alt_text, role')
        .eq('instrument_id', heroInstrument.id)
        .eq('is_visible', true)
        .order('display_order');
      return data?.find(i => i.role === 'hero') || data?.[0] || null;
    },
    enabled: !!heroInstrument,
  });

  const paths = useMemo(() => heroImage ? [heroImage.storage_path] : [], [heroImage]);
  const urls = useSignedUrls(paths);
  const dynamicUrl = heroImage ? urls.get(heroImage.storage_path) : '';

  const imageSrc = dynamicUrl || celloScroll;
  const imageAlt = heroImage?.alt_text || 'Handcrafted cello scroll — detail of fine craftsmanship by Szoradi Instruments';

  return (
    <section 
      className="relative min-h-screen overflow-hidden bg-background"
      aria-labelledby="hero-heading"
    >
      <img 
        src={imageSrc}
        alt={imageAlt}
        className="absolute inset-0 w-full h-full object-cover object-[80%_30%] md:object-[75%_center] lg:object-[70%_center]"
        width={1920}
        height={1080}
        fetchPriority="high"
      />

      <div 
        className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent lg:hidden" 
        aria-hidden="true" 
      />

      <div
        className="absolute inset-0 hidden lg:block"
        style={{
          background:
            'linear-gradient(to right, hsl(var(--background)) 12%, hsl(var(--background) / 0.78) 36%, hsl(var(--background) / 0.18) 58%, transparent 74%)',
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 min-h-screen flex items-end lg:items-center">
        <div className="w-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 xl:px-24 pb-20 sm:pb-24 lg:py-0">
          <div className="max-w-lg lg:max-w-xl">
            <h1 
              id="hero-heading" 
              className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium tracking-wide leading-[1.1] animate-fade-up text-white lg:text-foreground"
            >
              {t.brandName}
            </h1>

            <div 
              className="w-16 h-px mt-6 mb-6 sm:mt-8 sm:mb-8 animate-fade-up-delay-1 origin-left bg-white/50 lg:bg-gold"
              style={{ animation: 'revealLine 0.6s ease-out 0.3s forwards', transform: 'scaleX(0)' }}
              aria-hidden="true" 
            />

            <p className="font-serif text-xl sm:text-2xl leading-relaxed text-balance animate-fade-up-delay-1 text-white/90 lg:text-foreground/85">
              {t.tagline}
            </p>

            <p className="mt-4 sm:mt-5 text-sm sm:text-base leading-relaxed max-w-md animate-fade-up-delay-2 text-white/70 lg:text-muted-foreground">
              {t.subline}
            </p>

            {settings.homepage_hero_cta_visible && (
              <Link
                to="/contact"
                className="inline-block mt-8 sm:mt-10 px-10 py-4 border text-xs tracking-[0.25em] uppercase font-sans transition-all duration-300 animate-fade-up-delay-3 rounded-full border-white/30 text-white hover:bg-white hover:text-black lg:border-foreground/20 lg:text-foreground lg:hover:bg-foreground lg:hover:text-background"
              >
                {t.heroCtaContact}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
